import { inject } from '@angular/core';
import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { StoreService } from '../store/store.service';
import { from, Observable, switchMap } from 'rxjs';
import { DateTime } from 'luxon';

export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const store = inject(StoreService);
  const apiKey = environment.auth.apiKey;
  const authState = store.getStore().getState().auth;
  const accessToken = authState.avsApi?.accessToken;

  if (!req.url.includes(environment.api.avs)) {
    return next(req);
  }

  // If no token exists, proceed with the request without authentication
  if (!accessToken) {
    return next(req);
  }

  // Check if token is expired or will expire in the next 5 minutes
  const isTokenExpired =
    DateTime.fromSeconds(accessToken.expiresOn)
      .minus({ minutes: 5 })
      .diffNow()
      .as('seconds') < 0;

  // If token is not expired, proceed with the current token
  if (!isTokenExpired) {
    const authReq = addAuthHeaders(req, apiKey, accessToken.token);
    return next(authReq);
  }

  // If token is expired, try to refresh it
  return from(store.getActions().auth.refreshTokens()).pipe(
    switchMap((newToken) => {
      if (!newToken) {
        // Still return the original request to avoid breaking the HTTP chain
        return next(req);
      }

      // Proceed with the request using the new token
      const authReq = addAuthHeaders(req, apiKey, newToken);
      return next(authReq);
    }),
  );
}

function addAuthHeaders(
  req: HttpRequest<unknown>,
  apiKey: string,
  token: string,
): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      accept: 'application/json',
      APIKEY: apiKey,
      Authorization: `Bearer ${token}`,
    },
  });
}
